import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { toast } from "sonner";
import { formatError } from "@/lib/api";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

const grad = "linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const refCode = new URLSearchParams(location.search).get("ref") || "";
  const [form, setForm] = useState({ name: "", email: "", password: "", referral_code: refCode });
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success("Compte créé. Bienvenue !");
      nav("/admin");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const PERKS = [
    "Création de compte instantanée",
    "4 templates premium inclus",
    "Personnalisation totale (logo, couleurs)",
    "Wallet & cartes virtuelles",
    "Marketplace de domaines intégré",
  ];

  return (
    <div data-testid="register-root" style={{ minHeight: "100vh", background: "#f4f6ff", fontFamily: "Inter, system-ui, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem 1.5rem" }}>
      {/* Blobs */}
      <div style={{ position: "fixed", top: "-10rem", right: "-10rem", width: "500px", height: "500px", borderRadius: "50%", background: "rgba(139,92,246,.10)", filter: "blur(100px)", pointerEvents: "none" }} />
      <div style={{ position: "fixed", bottom: "-10rem", left: "-10rem", width: "400px", height: "400px", borderRadius: "50%", background: "rgba(59,130,246,.08)", filter: "blur(80px)", pointerEvents: "none" }} />

      <div style={{ position: "relative", width: "100%", maxWidth: "960px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3rem", alignItems: "stretch" }}
        className="register-grid">

        {/* Left panel — visible desktop only */}
        <div className="register-left" style={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <Link to="/" data-testid="register-back"
            style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 500, color: "#4a4a6a", textDecoration: "none" }}>
            <ArrowLeft size={14} /> Retour
          </Link>

          <div>
            <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "clamp(2rem, 4vw, 2.8rem)", letterSpacing: "-0.03em", lineHeight: 1.1, color: "#0d0d1a" }}>
              Devenez un<br />
              <span style={{ background: grad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                opérateur bancaire
              </span><br />
              en 3 minutes.
            </h1>

            <ul style={{ marginTop: "2.5rem", display: "flex", flexDirection: "column", gap: "1rem" }}>
              {PERKS.map((it) => (
                <li key={it} style={{ display: "flex", alignItems: "center", gap: "12px", color: "#0d0d1a", fontSize: "15px" }}>
                  <div style={{ width: "22px", height: "22px", borderRadius: "50%", background: "rgba(59,130,246,.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Check size={12} style={{ color: "#3B82F6" }} />
                  </div>
                  {it}
                </li>
              ))}
            </ul>

            {/* Testimonial */}
            <div style={{ marginTop: "2.5rem", padding: "1.25rem", borderRadius: "16px", background: "#fff", border: "1px solid #e2e8f8" }}>
              <div style={{ fontSize: "13px", color: "#4a4a6a", lineHeight: 1.6 }}>
                "J'ai lancé ma banque en moins de 10 minutes. Le template était déjà parfait."
              </div>
              <div style={{ marginTop: "0.75rem", display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: grad, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "11px", fontWeight: 700 }}>A</div>
                <div>
                  <div style={{ fontSize: "12px", fontWeight: 600, color: "#0d0d1a" }}>Aminata K.</div>
                  <div style={{ fontSize: "11px", color: "#4a4a6a" }}>Fondatrice — FinanceCI</div>
                </div>
              </div>
            </div>
          </div>

          <div style={{ fontSize: "12px", color: "#4a4a6a" }}>© 2026 Neobk.Pro</div>
        </div>

        {/* Right panel — form */}
        <div style={{ background: "#fff", border: "1px solid #e2e8f8", borderRadius: "24px", padding: "2.5rem", boxShadow: "0 8px 40px rgba(59,130,246,.08)" }}>
          {/* Mobile back */}
          <Link to="/" data-testid="register-back-mobile"
            className="register-back-mobile"
            style={{ display: "none", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 500, color: "#4a4a6a", textDecoration: "none", marginBottom: "1.5rem" }}>
            <ArrowLeft size={14} /> Retour
          </Link>

          {/* Logo */}
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "2rem" }}>
            <div style={{ width: "36px", height: "36px", borderRadius: "10px", background: grad, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: "16px", boxShadow: "0 4px 12px rgba(59,130,246,.4)" }}>N</div>
            <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "1.1rem", color: "#0d0d1a" }}>
              Neobk<span style={{ background: grad, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>.</span>Pro
            </span>
          </div>

          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "1.7rem", letterSpacing: "-0.02em", color: "#0d0d1a", marginBottom: "0.4rem" }}>
            Créer un compte
          </h2>
          <p style={{ color: "#4a4a6a", fontSize: "13px", marginBottom: "2rem" }}>Compte Admin — vous pourrez créer vos banques.</p>

          <form onSubmit={submit} data-testid="register-form" style={{ display: "flex", flexDirection: "column", gap: "1.1rem" }}>
            {[
              { label: "Nom complet", key: "name", type: "text", placeholder: "Aïcha Diakité", testId: "register-name" },
              { label: "Email", key: "email", type: "email", placeholder: "vous@example.com", testId: "register-email" },
              { label: "Mot de passe (min. 6 caractères)", key: "password", type: "password", placeholder: "••••••••", min: 6, testId: "register-password" },
            ].map(({ label, key, type, placeholder, min, testId }) => (
              <div key={key}>
                <label style={{ display: "block", fontSize: "13px", fontWeight: 600, color: "#0d0d1a", marginBottom: "6px" }}>{label}</label>
                <input
                  type={type} value={form[key]} onChange={upd(key)} required minLength={min}
                  placeholder={placeholder} data-testid={testId}
                  style={{ width: "100%", padding: "10px 14px", borderRadius: "10px", border: "1px solid #e2e8f8", fontSize: "14px", color: "#0d0d1a", outline: "none", background: "#f9faff", boxSizing: "border-box" }}
                  onFocus={(e) => e.target.style.borderColor = "#3B82F6"}
                  onBlur={(e) => e.target.style.borderColor = "#e2e8f8"}
                />
              </div>
            ))}

            <button
              type="submit" disabled={busy}
              data-testid="register-submit"
              style={{ width: "100%", padding: "12px", borderRadius: "12px", border: "none", cursor: busy ? "not-allowed" : "pointer", fontWeight: 600, fontSize: "15px", color: "#fff", background: busy ? "#a5b4fc" : grad, boxShadow: busy ? "none" : "0 8px 24px rgba(59,130,246,.4)", transition: "all .2s", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", marginTop: "0.5rem" }}>
              {busy ? "Création…" : <><span>Créer mon compte</span><ArrowRight size={16} /></>}
            </button>
          </form>

          <div style={{ marginTop: "1.5rem", textAlign: "center", fontSize: "13px", color: "#4a4a6a" }}>
            Déjà un compte ?{" "}
            <Link to="/login" data-testid="register-login-link" style={{ color: "#3B82F6", fontWeight: 600, textDecoration: "none" }}>
              Connexion
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .register-grid { grid-template-columns: 1fr !important; }
          .register-left { display: none !important; }
          .register-back-mobile { display: inline-flex !important; }
        }
      `}</style>
    </div>
  );
}
